import { useEffect, useState } from 'react'
import clsx from 'clsx'

/* blocks */
import Link from '@/atoms/Link/Link'
import Accordion from '@/atoms/Accordion/Accordion'
import { CategoriesDropdown } from './dropdowns'

/* utils */
import { HeaderProps as Props } from './Header.utils'

/* assets */
import { ReactComponent as PepperLogo } from 'public/assets/icons/PepperLogo.svg'

/* styling */
import styles from './mobile.module.css'

const MEDIUM_SCREEN_SIZE = 768

function MobileHeader({
  showNavLinks = true,
  className,
  primaryCta,
  secondaryCta,
  categoriesData,
  mode
}: Props): JSX.Element {
  const [menuVisibility, setMenuVisibility] = useState(false)
  const [activeAccordion, setActiveAccordion] = useState<string | number>()

  useEffect(() => {
    if (menuVisibility) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
      setActiveAccordion('')
    }
  }, [menuVisibility])

  useEffect(() => {
    // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/#css-custom-properties-the-trick-to-correct-sizing
    const getViewportHeight = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }

    getViewportHeight()

    if (window.innerWidth <= MEDIUM_SCREEN_SIZE) {
      window.addEventListener('resize', getViewportHeight)
    }
    return () => window.removeEventListener('resize', getViewportHeight)
  }, [])

  const toggleNav = () => setMenuVisibility(!menuVisibility)

  const handleAccordionChange = (key?: string | number, isOpen?: boolean) => {
    if (isOpen) setActiveAccordion(key)
    else setActiveAccordion('')
  }

  return (
    <section
      className={clsx(
        mode === 'dark' ? 'bg-blue-web-90 text-white' : 'bg-white text-gray-80',
        'sticky top-0  |  h-18  |  px-5 mx-auto  |  bg-white  |  z-50',
        menuVisibility && 'active',
        className
      )}
    >
      <div className="flex justify-between items-center | h-full">
        {showNavLinks ? (
          <Link href="/" className="pt-4 mb-6" title="Homepage">
            <PepperLogo style={{ width: 174, height: 33 }} />
          </Link>
        ) : (
          <PepperLogo style={{ width: 174, height: 33 }} />
        )}

        <div
          className={clsx(
            mode === 'dark' ? styles.hamburgerDarkBg : styles.hamburger,
            menuVisibility && 'active',
            !showNavLinks && 'hidden'
          )}
          role="button"
          aria-label="Toggle Navigation"
          tabIndex={0}
          id="hamburger"
          onClick={toggleNav}
          onKeyPress={toggleNav}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>

      {/* menu items */}
      <nav
        className={clsx(
          'bg-white | select-none | z-50 | overflow-y-auto',
          menuVisibility && 'active',
          styles.mobileNavDropdown
        )}
      >
        <Accordion
          className="accordion--lg font-heading"
          activeKey={activeAccordion}
          elementKey="Categories"
          title="Pepper Blogs"
          handleChange={handleAccordionChange}
          titleClass={styles.mobileNavTitle}
        >
          <CategoriesDropdown {...categoriesData} />
        </Accordion>

        <Link
          href="/case-study/"
          className={clsx('font-heading', styles.menuItem)}
        >
          Resources
        </Link>

        <Link href="/login/" className={clsx('font-heading', styles.menuItem)}>
          Login
        </Link>

        <div
          className={clsx(
            'grid grid-cols-1 md:grid-cols-2 justify-items-stretch gap-4 | fixed bottom-6 left-5 right-5  |  transition-all',
            activeAccordion && 'translate-y-20 | opacity-0 | invisible'
          )}
        >
          {secondaryCta ? (
            <Link
              href={secondaryCta.url}
              className="btn-secondary | btn-size-default whitespace-nowrap rounded-md"
            >
              {secondaryCta.title}
            </Link>
          ) : null}
          {primaryCta ? (
            <Link
              href={primaryCta.url}
              className="btn-primary | btn-size-default whitespace-nowrap rounded-md"
            >
              {primaryCta.title}
            </Link>
          ) : null}
        </div>
      </nav>
    </section>
  )
}

export default MobileHeader
